import React, { useState } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { borderRadius } from '@atlassian/jira-common-styles/src';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';
import { DeploymentStateOptions, DeploymentEnvironmentTypeOptions } from '../../../common/types';
import { getDeploymentIconState } from '../../../common/ui/deployment-icon';
import { getColorForDeploymentState } from '../../../common/utils/deployments';
import { useIconsConfigurationPreference } from '../../../services/my-preference';
import Key from './key';
import messages from './messages';

export const Legend = () => {
	const { formatMessage } = useIntl();
	const iconsConfig = useIconsConfigurationPreference();
	const [keyExpanded, setKeyExpanded] = useState(false);

	if (iconsConfig === null || !iconsConfig.showIconKey) {
		return null;
	}

	const KeysWrapper = ff('a11y-jira-team.deployment-filter-and-table_m845b')
		? KeysWrapperNew
		: KeysWrapperOld;

	return (
		<ContentContainer data-testid="software-deployments.ui.timeline.legend.table-key-legend">
			<OptionsWrapper>
				<LabelWrapper>{formatMessage(messages.legendLabel)}</LabelWrapper>
				<Button
					testId="software-deployments.ui.timeline.legend.legend-button"
					appearance="subtle-link"
					spacing="none"
					onClick={() => setKeyExpanded(!keyExpanded)}
				>
					<ShowMoreWrapper
						onClick={() => setKeyExpanded(!keyExpanded)}
						data-testid="software-deployments.ui.timeline.legend.show-more"
					>
						{keyExpanded ? formatMessage(messages.showLess) : formatMessage(messages.showMore)}
					</ShowMoreWrapper>
				</Button>
			</OptionsWrapper>
			<KeysWrapper>
				{keyExpanded && (
					<Key
						color={getColorForDeploymentState(DeploymentStateOptions.IN_PROGRESS)}
						state={getDeploymentIconState(
							DeploymentStateOptions.IN_PROGRESS,
							DeploymentEnvironmentTypeOptions.PRODUCTION,
						)}
					>
						{formatMessage(messages.inProgress)}
					</Key>
				)}
				{keyExpanded && (
					<Key
						color={getColorForDeploymentState(DeploymentStateOptions.FAILED)}
						state={getDeploymentIconState(
							DeploymentStateOptions.FAILED,
							DeploymentEnvironmentTypeOptions.PRODUCTION,
						)}
					>
						{formatMessage(messages.failed)}
					</Key>
				)}
				{keyExpanded && (
					<Key
						color={getColorForDeploymentState(DeploymentStateOptions.ROLLED_BACK)}
						state={getDeploymentIconState(
							DeploymentStateOptions.ROLLED_BACK,
							DeploymentEnvironmentTypeOptions.PRODUCTION,
						)}
					>
						{formatMessage(messages.rolledBack)}
					</Key>
				)}
				{keyExpanded && (
					<Key
						color={getColorForDeploymentState(DeploymentStateOptions.PENDING)}
						state={getDeploymentIconState(
							DeploymentStateOptions.PENDING,
							DeploymentEnvironmentTypeOptions.PRODUCTION,
						)}
					>
						{formatMessage(messages.pending)}
					</Key>
				)}
				{keyExpanded && (
					<Key
						color={getColorForDeploymentState(DeploymentStateOptions.CANCELLED)}
						state={getDeploymentIconState(
							DeploymentStateOptions.CANCELLED,
							DeploymentEnvironmentTypeOptions.PRODUCTION,
						)}
					>
						{formatMessage(messages.cancelled)}
					</Key>
				)}
				<Key
					color={getColorForDeploymentState(DeploymentStateOptions.SUCCESSFUL)}
					state={getDeploymentIconState(
						DeploymentStateOptions.SUCCESSFUL,
						DeploymentEnvironmentTypeOptions.STAGING,
					)}
				>
					{formatMessage(messages.nonProduction)}
				</Key>
				<Key
					color={getColorForDeploymentState(DeploymentStateOptions.SUCCESSFUL)}
					state={getDeploymentIconState(
						DeploymentStateOptions.SUCCESSFUL,
						DeploymentEnvironmentTypeOptions.PRODUCTION,
					)}
				>
					{formatMessage(messages.production)}
				</Key>
			</KeysWrapper>
		</ContentContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ContentContainer = styled.div({
	display: 'flex',
	justifyContent: 'space-evenly',
	flexDirection: 'column',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
	boxSizing: 'content-box',
	padding: `${token('space.075', '6px')} ${token('space.150', '12px')} 0px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('elevation.surface.raised', colors.N0),
	width: '208px',
	boxShadow: token(
		'elevation.shadow.raised',
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
		`0 4px 8px -2px ${colors.N50A}, 0 0 1px ${colors.N60A}`,
	),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const KeysWrapperOld = styled.ul({
	display: 'flex',
	flexDirection: 'column',
	listStyleType: 'none',
	marginTop: token('space.050', '4px'),
	padding: 0,
	width: '100%',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const KeysWrapperNew = styled.ul({
	display: 'flex',
	flexDirection: 'column',
	listStyleType: 'none',
	marginTop: token('space.050', '4px'),
	padding: 0,
	width: '100%',
	overflow: 'auto',
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const OptionsWrapper = styled.div({
	display: 'flex',
	width: '100%',
	alignItems: 'center',
	justifyContent: 'space-between',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const LabelWrapper = styled.small({
	marginTop: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N200),
	fontWeight: 'bold',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ShowMoreWrapper = styled.small({
	marginTop: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text', colors.N200),
	fontWeight: 'bold',
});
